import { useQuery } from "@tanstack/react-query";
import { API_BASE_URL, API_ENDPOINTS } from "../constants/index";

// API Service class
class ApiService {
  constructor(endpoint) {
    this.endpoint = endpoint;
    this.baseUrl = `${API_BASE_URL}${endpoint}`;
  }

  async get(id = null, params = {}) {
    try {
      const url = new URL(id ? `${this.baseUrl}/${id}` : this.baseUrl);
      Object.keys(params).forEach((key) =>
        url.searchParams.append(key, params[key])
      );

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error(`Error fetching ${this.endpoint}:`, error);
      throw error;
    }
  }
}

// Individual hooks for each master data
export const useCustomers = () => {
  return useQuery({
    queryKey: ["customers"],
    queryFn: async () => {
      const api = new ApiService(API_ENDPOINTS.CUSTOMERMASTERACTIVELIST);
      const response = await api.get(null, { status: 1 });

      if (response.status === 200) {
        return response.data.map((item) => ({
          value: item.customer_code,
          label: item.customer_name,
          contact: item.poc_name,
          phone: item.ph_no,
          email: item.email,
        }));
      }
      return [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useVendors = () => {
  return useQuery({
    queryKey: ["vendors"],
    queryFn: async () => {
      const api = new ApiService(API_ENDPOINTS.VENDORMASTERACTIVELIST);
      const response = await api.get(null, { status: 1 });

      if (response.status === 200) {
        return response.data.map((item) => ({
          value: item.vendor_code,
          label: item.vendor_name,
          contact: item.poc_name,
          phone: item.ph_no,
          type: "Vendor",
        }));
      }
      return [];
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });
};

export const usePorts = () => {
  return useQuery({
    queryKey: ["ports"],
    queryFn: async () => {
      const api = new ApiService(API_ENDPOINTS.PORTMASTERACTIVELIST);
      const response = await api.get(null, { status: 1 });

      if (response.status === 200) {
        return response.data.map((item) => ({
          value: item.port_id.toString(),
          label: item.port_name,
          location: `${item.city}, ${item.state}`,
          code: item.port_code,
        }));
      }
      return [];
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });
};

export const useWarehouses = () => {
  return useQuery({
    queryKey: ["warehouses"],
    queryFn: async () => {
      const api = new ApiService(API_ENDPOINTS.WAREHOUSEMASTERACTIVELIST);
      const response = await api.get(null, { status: 1 });

      if (response.status === 200) {
        return response.data.map((item) => ({
          value: item.wh_id.toString(),
          label: item.wh_name,
          address: `${item.address}, ${item.city} - ${item.pincode}`,
          capacity: item.capacity,
        }));
      }
      return [];
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });
};

export const useTransporters = () => {
  return useQuery({
    queryKey: ["transporters"],
    queryFn: async () => {
      const api = new ApiService(API_ENDPOINTS.TRANSPORTERMASTERACTIVELIST);
      const response = await api.get(null, { status: 1 });

      if (response.status === 200) {
        return response.data.map((item) => ({
          value: item.transpoter_code,
          label: item.name,
          code: item.transpoter_code,
          contact: item.poc_name,
          phone: item.poc_no,
        }));
      }
      return [];
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });
};

export const useContainers = () => {
  return useQuery({
    queryKey: ["containers"],
    queryFn: async () => {
      const api = new ApiService(API_ENDPOINTS.CONTAINERMASTERACTIVELIST);
      const response = await api.get(null, { status: 1 });

      if (response.status === 200) {
        return response.data.map((item) => ({
          value: item.container_id.toString(),
          label: item.container_code?.trim(),
          type: item.size,
          owner: item.company_name,
          tare: 2200,
          maxWeight: item.max_capacity,
          location: "Mumbai Port",
          freeTime: 72,
          detentionRate: 500,
        }));
      }
      return [];
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });
};

export const useGoodsTypes = () => {
  return useQuery({
    queryKey: ["goodsTypes"],
    queryFn: async () => {
      const api = new ApiService(API_ENDPOINTS.GOODSTYPEMASTERACTIVELIST);
      const response = await api.get(null, { status: 1 });

      if (response.status === 200) {
        return response.data.map((item) => ({
          value: item.goods_id,
          label: item.goods_name,
        }));
      }
      return [];
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });
};

export const useVehiclesByTransporter = (transporterCode) => { 
  return useQuery({
    queryKey: ["vehicles", transporterCode],
    queryFn: async () => {
      if (!transporterCode) return [];

      const api = new ApiService(API_ENDPOINTS.TRANSPORTERWISEVEHICLELIST);
      const response = await api.get(null, {
        transporter_code: transporterCode,
      });

      if (response.status === 200) {
        return response?.data?.map((item) => ({
          value: item.vehicle_id?.toString(),
          label: item.vehicle_no,
          type: item.vehicle_type,
          desc: item.desc,
          capacity: item.max_capacity,
        }));
      }
      return [];
    },
    enabled: !!transporterCode,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });
};

// --- add this to your master-data-hooks file (near the other hooks) ---
export const useEsanchitCodes = (consignmentType) => {
  return useQuery({
    queryKey: ["esanchitCodes", consignmentType],
    queryFn: async () => {
      const api = new ApiService(API_ENDPOINTS.ESANCHITCODELIST); // ensure this endpoint exists in API_ENDPOINTS
      const response = await api.get(null, { status: 1 });

      if (response.status === 200) {
        // convert to select options and filter by consignmentType if provided
        const items = response.data || [];
        const filtered = consignmentType
          ? items.filter(
              (it) =>
                (it.consignment_type || "")
                  .toString()
                  .toLowerCase() === consignmentType.toString().toLowerCase()
            )
          : items;

        return filtered.map((item) => ({
          value: item.esanchit_code || item.id,
          label: item.esanchit_code || `${item.id}`,
          raw: item,
        }));
      }
      return [];
    },
    // re-fetch when consignmentType changes (queryKey already includes it)
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });
};


// Combined hook for all master data
export const useMasterData = () => {
  const customers = useCustomers();
  const vendors = useVendors();
  const ports = usePorts();
  const warehouses = useWarehouses();
  const transporters = useTransporters();
  const containers = useContainers();
  const goodsTypes = useGoodsTypes();
  const vehiclesByTransporter = useVehiclesByTransporter();

  return {
    customers: customers.data || [],
    vendors: vendors.data || [],
    ports: ports.data || [],
    warehouses: warehouses.data || [],
    transporters: transporters.data || [],
    containers: containers.data || [],
    goodsTypes: goodsTypes.data || [],
    vehiclesByTransporter: vehiclesByTransporter.data || [],
    isLoading:
      customers.isLoading ||
      vendors.isLoading ||
      ports.isLoading ||
      warehouses.isLoading ||
      transporters.isLoading ||
      containers.isLoading ||
      goodsTypes.isLoading,
    error:
      customers.error ||
      vendors.error ||
      ports.error ||
      warehouses.error ||
      transporters.error ||
      containers.error ||
      goodsTypes.error,
  };
};

export { ApiService };